import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { FormClose, Previous, Refresh } from 'grommet-icons';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  AppButton,
  Spinning,
  SimpleModal,
  SubmissionLoader,
} from '@Components/Control';
import {
  ProductFormDropdownSelect,
  ProductFormSingleTextInput,
} from '@Components/Partial/Product/NewSearch';
import {
  StyledFormTextArea,
  StyledUploadContainer,
  StyledUploadInput,
} from '@Components/Partial/Product/NewSearch/StyledProductFormComponent.jsx';
import { NoDataPlaceholder } from '@Components/Partial/Product/SharedComponents.jsx';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  fetchNagraCandidate,
  updateNagraCandidate,
  updateNagraCandidateStatus,
  uploadNagraScreenshot,
  submitNagraCandidate,
} from '@Actions';

// --- Helpers ---
const isValidUrl = (val) => {
  if (!val?.trim()) return false;
  try { return Boolean(new URL(val.trim())); } catch { return false; }
};

// --- Constants ---
const STREAM_TYPES = ['hls', 'p2p', 'rtmp'];

const NAGRA_ORIGIN_TYPES = [
  'ReferenceSite',
  'SearchEngine',
  'SocialMedia',
  'P2P',
  'SearchFakeResults',
  'MessagingApp',
  'MobileApp',
  'UGC',
  'MarketPlace',
  'FreeM3UList',
];

// Metadata fields shown per origin (mirrors manual submission page)
const ORIGIN_METADATA_FIELDS = {
  SocialMedia: [
    'contentType', 'country', 'language', 'subscribers', 'channelStatus',
    'claimType', 'viewsDownloads', 'videoDuration',
    'infringementPublishingTimestamp', 'domainName', 'serverName',
    'serverLocation', 'identificationTimestamp', 'nexguardWatermarkId',
    'searchString',
  ],
  UGC: [
    'contentType', 'country', 'language', 'subscribers', 'channelStatus',
    'claimType', 'viewsDownloads', 'videoDuration',
    'infringementPublishingTimestamp', 'domainName', 'serverName',
    'serverLocation', 'identificationTimestamp', 'nexguardWatermarkId',
    'searchString',
  ],
  MessagingApp: [
    'contentType', 'country', 'language', 'channelType', 'channelStatus',
    'subscribers', 'viewsDownloads', 'infringementPublishingTimestamp',
    'domainName', 'serverName', 'serverLocation', 'identificationTimestamp',
    'nexguardWatermarkId', 'searchString',
  ],
  FreeM3UList: [
    'contentType', 'country', 'language', 'searchString',
    'domainName', 'serverName', 'serverLocation',
    'identificationTimestamp', 'viewsDownloads',
  ],
  ReferenceSite: [
    'contentType', 'country', 'language', 'domainName', 'serverName',
    'serverLocation', 'identificationTimestamp', 'nexguardWatermarkId',
    'searchString',
  ],
  SearchEngine: [
    'country', 'language', 'domainName', 'serverName', 'serverLocation',
    'identificationTimestamp', 'searchString',
  ],
};

const METADATA_FIELD_LABELS = {
  contentType: 'Content Type',
  country: 'Country',
  language: 'Language',
  subscribers: 'Subscribers',
  channelStatus: 'Channel Status',
  claimType: 'Claim Type',
  viewsDownloads: 'Views / Downloads',
  videoDuration: 'Video Duration',
  infringementPublishingTimestamp: 'Infringement Publishing Timestamp',
  domainName: 'Domain Name',
  serverName: 'Server Name',
  serverLocation: 'Server Location',
  identificationTimestamp: 'Identification Timestamp',
  nexguardWatermarkId: 'NexGuard Watermark ID',
  searchString: 'Search String',
  channelType: 'Channel Type (Public/Private)',
};

// snake_case overrides for fields where naive conversion is wrong
const METADATA_SNAKE_OVERRIDES = {
  nexguardWatermarkId: 'nexguard_watermark_id',
};

const NAGRA_CONTENT_TYPES = [
  'LIVE - Full Pirate Live Stream',
  'LIVE - Sports Highlights',
  'VOD - Full Pirate Stream',
  'VOD - Clip Pirate Stream',
];

const REVIEW_STATUS_COLORS = {
  pending: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
  'in-review': { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  approved: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  rejected: { bg: '#e53e3e22', border: '#e53e3e', text: '#e53e3e' },
  submitted: { bg: '#8b5cf622', border: '#8b5cf6', text: '#8b5cf6' },
};

// --- Sub-components ---
/* eslint-disable react/prop-types */
const Section = ({ title, children, sectionBg }) => (
  <Box
    direction="column"
    gap="1rem"
    pad="1.5rem"
    round="var(--app-radius)"
    background={sectionBg}
    style={{ boxShadow: 'var(--elevation)' }}
    height="fit-content"
  >
    <Text size="1rem" weight={600}>
      {title}
    </Text>
    {children}
  </Box>
);

const FieldCol = ({
  label, children, page, required,
}) => (
  <Box direction="column" gap="0.4rem">
    <Text size="0.8rem" color={page.hintText}>
      {label}
      {required && <Text color="var(--app-error)"> *</Text>}
    </Text>
    {children}
  </Box>
);

const ReadOnlyField = ({
  label, value, page, mono,
}) => (
  <Box direction="column" gap="0.3rem">
    <Text size="0.8rem" color={page.hintText}>{label}</Text>
    <Text
      size="0.9rem"
      color={page.primaryText}
      style={mono ? { fontFamily: 'monospace', wordBreak: 'break-all' } : { wordBreak: 'break-word' }}
    >
      {value ?? '—'}
    </Text>
  </Box>
);

const StatusBadge = ({ value }) => {
  if (!value) return <Text size="0.9rem">—</Text>;
  const colors = REVIEW_STATUS_COLORS[value] || { bg: '#71717a22', border: '#71717a', text: '#71717a' };
  const label = String(value).replace(/-/g, ' ');
  return (
    <Box
      background={colors.bg}
      border={{ color: colors.border, size: '1px' }}
      round="6px"
      pad={{ vertical: '0.2rem', horizontal: '0.6rem' }}
      width="fit-content"
    >
      <Text size="0.85rem" weight={600} color={colors.text}>{label}</Text>
    </Box>
  );
};
/* eslint-enable react/prop-types */

// --- Main Page ---
const AdminNagraCandidateDetailPage = ({
  small,
  location,
  candidate = null,
  loading,
  saving,
  submitting,
  uploadingScreenshot,
  fetchCandidate,
  saveCandidate,
  saveCandidateStatus,
  uploadScreenshot,
  submitCandidate,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const [candidateForm, setCandidateForm] = React.useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState({});
  const [isShowingRejectModal, setIsShowingRejectModal] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [rejectionError, setRejectionError] = React.useState('');
  const [pendingFile, setPendingFile] = React.useState(null);
  const [fileError, setFileError] = React.useState('');
  const fileInputRef = React.useRef(null);

  const reload = React.useCallback(() => {
    if (id) fetchCandidate(id);
  }, [id, fetchCandidate]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  React.useEffect(() => {
    if (candidate) {
      setCandidateForm({
        videoUrl: candidate.videoUrl?.url ?? '',
        referenceUrl: candidate.referenceUrl?.url ?? '',
        viewingPageUrl: candidate.viewingPageUrl?.url ?? '',
        playerPageUrl: candidate.playerPageUrl?.url ?? '',
        type: candidate.type ?? '',
        channelName: candidate.channelName ?? '',
        origin: candidate.origin ?? '',
        playCommand: candidate.playCommand ?? '',
        // metadata — load all possible fields
        contentType: candidate.metadata?.contentType ?? '',
        country: candidate.metadata?.country ?? '',
        language: candidate.metadata?.language ?? '',
        domainName: candidate.metadata?.domainName ?? '',
        serverName: candidate.metadata?.serverName ?? '',
        serverLocation: candidate.metadata?.serverLocation ?? '',
        searchString: candidate.metadata?.searchString ?? '',
        nexguardWatermarkId: candidate.metadata?.nexguardWatermarkId ?? '',
        identificationTimestamp: candidate.metadata?.identificationTimestamp ?? '',
        subscribers: candidate.metadata?.subscribers ?? '',
        channelStatus: candidate.metadata?.channelStatus ?? '',
        claimType: candidate.metadata?.claimType ?? '',
        viewsDownloads: candidate.metadata?.viewsDownloads ?? '',
        videoDuration: candidate.metadata?.videoDuration ?? '',
        infringementPublishingTimestamp: candidate.metadata?.infringementPublishingTimestamp ?? '',
        channelType: candidate.metadata?.channelType ?? '',
      });
      setHasUnsavedChanges(false);
    }
  }, [candidate]);

  const handleChange = (field, value) => {
    setCandidateForm((prev) => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = (form) => {
    const errors = {};
    if (!form.videoUrl?.trim()) {
      errors.videoUrl = 'Video URL is required.';
    } else if (!isValidUrl(form.videoUrl)) {
      errors.videoUrl = 'Video URL must be a valid URL (e.g. https://example.com/stream.m3u8).';
    }
    if (!form.viewingPageUrl?.trim()) {
      errors.viewingPageUrl = 'Viewing Page URL is required.';
    } else if (!isValidUrl(form.viewingPageUrl)) {
      errors.viewingPageUrl = 'Viewing Page URL must be a valid URL (e.g. https://example.com/watch).';
    }
    if (form.referenceUrl?.trim() && !isValidUrl(form.referenceUrl)) {
      errors.referenceUrl = 'Reference URL must be a valid URL.';
    }
    if (form.playerPageUrl?.trim() && !isValidUrl(form.playerPageUrl)) {
      errors.playerPageUrl = 'Player Page URL must be a valid URL.';
    }
    if (!form.origin) {
      errors.origin = 'Origin is required.';
    }
    return errors;
  };

  const handleSave = () => {
    if (!candidateForm) return;
    const errors = validateForm(candidateForm);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    /* eslint-disable camelcase */
    const updates = {};
    if (candidateForm.videoUrl?.trim()) updates.video_url = { url: candidateForm.videoUrl.trim() };
    if (candidateForm.referenceUrl?.trim()) updates.reference_url = { url: candidateForm.referenceUrl.trim() };
    if (candidateForm.viewingPageUrl?.trim()) updates.viewing_page_url = { url: candidateForm.viewingPageUrl.trim() };
    if (candidateForm.playerPageUrl?.trim()) updates.player_page_url = { url: candidateForm.playerPageUrl.trim() };
    if (candidateForm.type) updates.type = candidateForm.type;
    if (candidateForm.origin) updates.origin = candidateForm.origin;
    updates.channel_name = candidateForm.channelName ?? '';
    updates.play_command = candidateForm.playCommand ?? '';

    // Write all metadata fields using dot-notation so MongoDB $set patches
    // individual sub-fields without wiping ones not shown in the form.
    const allMetaFields = Object.keys(METADATA_FIELD_LABELS);
    allMetaFields.forEach((field) => {
      const snakeField = METADATA_SNAKE_OVERRIDES[field]
        ?? field.replace(/([A-Z])/g, '_$1').toLowerCase();
      updates[`metadata.${snakeField}`] = candidateForm[field]?.trim?.() || null;
    });
    /* eslint-enable camelcase */
    saveCandidate(id, updates);
    setHasUnsavedChanges(false);
  };

  const handleFileSelect = (e) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;
    setFileError('');
    const allowedTypes = ['image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setFileError('Only JPEG and PNG images are accepted. Please select a valid image file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setFileError('The selected file exceeds the 10 MB size limit. Please choose a smaller image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setPendingFile(file);
  };

  const handleUploadConfirm = () => {
    if (!pendingFile) return;
    setFileError('');
    uploadScreenshot(id, pendingFile);
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveFile = () => {
    setPendingFile(null);
    setFileError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      setRejectionError('A rejection reason is required before this record can be rejected.');
      return;
    }
    setRejectionError('');
    /* eslint-disable camelcase */
    saveCandidateStatus(id, {
      review_status: 'rejected',
      rejection_reason: rejectionReason.trim(),
    });
    /* eslint-enable camelcase */
    setIsShowingRejectModal(false);
    setRejectionReason('');
  };

  const reviewStatus = candidate?.reviewStatus ?? '';
  const screenshots = candidate?.screenshotS3Urls ?? [];

  // Approve & Submit — requires a screenshot; blocked once submitted or rejected
  const approveDisabled = screenshots.length === 0
    || reviewStatus === 'submitted'
    || reviewStatus === 'rejected'
    || submitting
    || saving;

  const rejectDisabled = reviewStatus === 'rejected'
    || reviewStatus === 'submitted'
    || submitting
    || saving;

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Candidate Detail',
        subTitle: 'Review and edit candidate before submission',
        bannerChildrenPosition: 'bottom',
        bannerChildren: !loading && (
          <Box width="fit-content" direction="row" gap="1rem">
            <AppButton
              overrideHover
              onClick={() => navigate(paths.adminNagraReviewCandidates)}
              label="Back to candidates"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Previous size="0.8rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
            />
            <AppButton
              overrideHover
              onClick={reload}
              label="Refresh"
              level="tableControls"
              bgColor={isDarkMode ? button.primaryBg : page.altBg}
              color={isDarkMode ? button.primaryText : page.primaryText}
              icon={<Refresh size="0.9rem" color={isDarkMode ? button.primaryText : page.primaryText} />}
              disabled={loading || saving}
            />
          </Box>
        ),
      }}
    >
      <Seo />
      {(saving || submitting) && (
        <SubmissionLoader
          textColor={page.primaryText}
          loaderColor={page.highlight}
          message={submitting ? 'Submitting to NAGRA...' : 'Saving changes...'}
          contentWidth="20rem"
        />
      )}
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '2rem' }}
        gap="1.5rem"
        width="100%"
      >
        {loading && (
          <Box align="center" justify="center" pad="large" flex>
            <Spinning size="large" color={page.highlight} />
          </Box>
        )}
        {!loading && !candidate && (
          <NoDataPlaceholder
            loading={false}
            width="100%"
            label="Candidate not found."
            textColor={page.primaryText}
            loaderColor={page.highlight}
          />
        )}
        {!loading && candidate && candidateForm && (
          <>
            {/* Two-column layout — left (grow 3) | right (grow 2) */}
            <Box direction={small ? 'column' : 'row'} gap="1.5rem" align="stretch">
              {/* LEFT column: Stream URLs + Broadcaster & Stream Info + Review Status + Screenshot */}
              <Box style={{ flex: '1 1 0', minWidth: 0 }} direction="column" gap="1.5rem">
                {/* Stream URLs */}
                <Section title="Stream URLs" sectionBg={useSectionBg}>
                  <FieldCol label="Video URL" page={page} required>
                    <ProductFormSingleTextInput
                      value={candidateForm.videoUrl}
                      handleFormValues={(val) => handleChange('videoUrl', val)}
                      placeholder="https://..."
                      error={!!formErrors.videoUrl}
                    />
                    {formErrors.videoUrl && (
                      <Text size="0.75rem" color="var(--app-error)">{formErrors.videoUrl}</Text>
                    )}
                  </FieldCol>
                  <FieldCol label="Reference URL" page={page}>
                    <ProductFormSingleTextInput
                      value={candidateForm.referenceUrl}
                      handleFormValues={(val) => handleChange('referenceUrl', val)}
                      placeholder="https://..."
                      error={!!formErrors.referenceUrl}
                    />
                    {formErrors.referenceUrl && (
                      <Text size="0.75rem" color="var(--app-error)">{formErrors.referenceUrl}</Text>
                    )}
                  </FieldCol>
                  <FieldCol label="Viewing Page URL" page={page} required>
                    <ProductFormSingleTextInput
                      value={candidateForm.viewingPageUrl}
                      handleFormValues={(val) => handleChange('viewingPageUrl', val)}
                      placeholder="https://..."
                      error={!!formErrors.viewingPageUrl}
                    />
                    {formErrors.viewingPageUrl && (
                      <Text size="0.75rem" color="var(--app-error)">{formErrors.viewingPageUrl}</Text>
                    )}
                  </FieldCol>
                  <FieldCol label="Player Page URL (optional)" page={page}>
                    <ProductFormSingleTextInput
                      value={candidateForm.playerPageUrl}
                      handleFormValues={(val) => handleChange('playerPageUrl', val)}
                      placeholder="https://..."
                      error={!!formErrors.playerPageUrl}
                    />
                    {formErrors.playerPageUrl && (
                      <Text size="0.75rem" color="var(--app-error)">{formErrors.playerPageUrl}</Text>
                    )}
                  </FieldCol>
                  <FieldCol label="Stream Type" page={page}>
                    <ProductFormDropdownSelect
                      selectable
                      simpleOptions={STREAM_TYPES}
                      keepLabel
                      value={candidateForm.type}
                      handleFormValues={(val) => handleChange('type', val)}
                    />
                  </FieldCol>
                </Section>
                {/* Broadcaster & Stream Info */}
                <Section title="Broadcaster & Stream Info" sectionBg={useSectionBg}>
                  <FieldCol label="Channel Name" page={page}>
                    <ProductFormSingleTextInput
                      value={candidateForm.channelName}
                      handleFormValues={(val) => handleChange('channelName', val)}
                    />
                  </FieldCol>
                  <FieldCol label="Origin" page={page} required>
                    <ProductFormDropdownSelect
                      selectable
                      simpleOptions={NAGRA_ORIGIN_TYPES}
                      keepLabel
                      value={candidateForm.origin}
                      handleFormValues={(val) => handleChange('origin', val)}
                    />
                    {formErrors.origin && (
                      <Text size="0.75rem" color="var(--app-error)">{formErrors.origin}</Text>
                    )}
                  </FieldCol>
                  {candidateForm.type === 'rtmp' && (
                    <FieldCol label="Play Command" page={page}>
                      <ProductFormSingleTextInput
                        value={candidateForm.playCommand}
                        handleFormValues={(val) => handleChange('playCommand', val)}
                        placeholder="ffplay rtmp://..."
                      />
                    </FieldCol>
                  )}
                </Section>
                {/* Review Status */}
                <Section title="Review Status" sectionBg={useSectionBg}>
                  <Box direction="row" gap="1.5rem" wrap>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <ReadOnlyField label="Source" value={candidate.source} page={page} />
                    </Box>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <ReadOnlyField
                        label="Edited by QA"
                        value={(() => {
                          if (candidate.wasEditedByQa == null) return null;
                          return candidate.wasEditedByQa ? 'Yes' : 'No';
                        })()}
                        page={page}
                      />
                    </Box>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <Box direction="column" gap="0.3rem">
                        <Text size="0.8rem" color={page.hintText}>Status</Text>
                        <StatusBadge value={candidate.reviewStatus} />
                      </Box>
                    </Box>
                  </Box>
                  {candidate.reviewStatus === 'rejected' && (
                    <ReadOnlyField label="Rejection Reason" value={candidate.rejectionReason} page={page} />
                  )}
                  {candidate.nagraCandidateUuid && (
                    <ReadOnlyField label="NAGRA UUID" value={candidate.nagraCandidateUuid} page={page} mono />
                  )}
                </Section>
                {/* Screenshot */}
                <Section title="Screenshot" sectionBg={useSectionBg}>
                  {screenshots.length === 0 && (
                    <Text size="0.85rem" color="var(--app-error)">
                      A screenshot is required before this candidate can be approved and submitted.
                    </Text>
                  )}
                  {screenshots.length > 0 && (
                    <Box direction="column" gap="0.5rem">
                      <Box
                        round="var(--app-radius)"
                        overflow="hidden"
                        border={{ color: page.tableBorder, size: '1px' }}
                        width="160px"
                        height="120px"
                      >
                        <img
                          src={screenshots[0]}
                          alt="Screenshot"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                      <Text size="0.75rem" color={page.hintText}>
                        Screenshot uploaded. You can replace it by uploading a new file below.
                      </Text>
                    </Box>
                  )}
                  <Box direction="column" gap="0.5rem">
                    <Text size="0.8rem" color={page.hintText}>
                      {screenshots.length > 0
                        ? 'Replace screenshot — must show the viewing page with the video actively playing, no ads visible. Accepted formats: JPEG, PNG. Maximum file size: 10 MB.'
                        : 'Upload a screenshot of the viewing page with the video actively playing and no ads visible. Accepted formats: JPEG, PNG. Maximum file size: 10 MB.'}
                    </Text>
                    {fileError && (
                      <Text size="0.75rem" color="var(--app-error)">{fileError}</Text>
                    )}
                    <Box direction="row" gap="1rem" align="center" width="100%">
                      <StyledUploadContainer highlight={page.focusHighlight}>
                        <Box fill gap="1rem" align="center" direction="row">
                          <Text truncate size="0.875rem">
                            {pendingFile ? pendingFile.name : 'No file selected'}
                          </Text>
                          <AppButton
                            level="xsmall"
                            label="Choose File..."
                            color={page.focusHighlight}
                            disabled={uploadingScreenshot}
                          />
                        </Box>
                        <StyledUploadInput
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png"
                          disabled={uploadingScreenshot}
                          onChange={handleFileSelect}
                          highlight={page.focusHighlight}
                        />
                      </StyledUploadContainer>
                      {pendingFile && (
                        <AppButton
                          plain
                          icon={<FormClose size="1.25rem" color={button.iconColor} />}
                          onClick={handleRemoveFile}
                        />
                      )}
                    </Box>
                    {pendingFile && !uploadingScreenshot && (
                      <Box width="fit-content">
                        <AppButton
                          overrideHover
                          level="normal"
                          label={screenshots.length > 0 ? 'Replace Screenshot' : 'Upload Screenshot'}
                          bgColor={button.primaryBg}
                          color={button.primaryText}
                          onClick={handleUploadConfirm}
                        />
                      </Box>
                    )}
                    {uploadingScreenshot && (
                      <Box direction="row" gap="0.5rem" align="center">
                        <Spinning size="small" color={page.highlight} />
                        <Text size="0.8rem" color={page.hintText}>Uploading...</Text>
                      </Box>
                    )}
                  </Box>
                </Section>
              </Box>
              {/* RIGHT column: Metadata only */}
              <Box style={{ flex: '1 1 0', minWidth: 0 }} direction="column" gap="1.5rem">
                {/* Metadata */}
                <Section title="Metadata" sectionBg={useSectionBg}>
                  <Text size="0.78rem" color={page.hintText}>
                    All metadata fields are optional. NAGRA accepts and stores
                    every field listed here — fill in as many as you have available.
                  </Text>
                  {(ORIGIN_METADATA_FIELDS[candidateForm.origin] ?? []).map((field) => {
                    const label = `${METADATA_FIELD_LABELS[field] ?? field} (optional)`;
                    if (field === 'contentType') {
                      return (
                        <FieldCol key={field} label={label} page={page}>
                          <ProductFormDropdownSelect
                            selectable
                            simpleOptions={['', ...NAGRA_CONTENT_TYPES]}
                            keepLabel
                            value={candidateForm.contentType}
                            handleFormValues={(val) => handleChange('contentType', val)}
                          />
                        </FieldCol>
                      );
                    }
                    return (
                      <FieldCol key={field} label={label} page={page}>
                        <ProductFormSingleTextInput
                          value={candidateForm[field] ?? ''}
                          handleFormValues={(val) => handleChange(field, val)}
                        />
                      </FieldCol>
                    );
                  })}
                  {!ORIGIN_METADATA_FIELDS[candidateForm.origin] && (
                    // Fallback for origins without a specific field map
                    <>
                      <FieldCol label="Content Type (optional)" page={page}>
                        <ProductFormDropdownSelect
                          selectable
                          simpleOptions={['', ...NAGRA_CONTENT_TYPES]}
                          keepLabel
                          value={candidateForm.contentType}
                          handleFormValues={(val) => handleChange('contentType', val)}
                        />
                      </FieldCol>
                      <FieldCol label="Country (optional)" page={page}>
                        <ProductFormSingleTextInput
                          value={candidateForm.country}
                          handleFormValues={(val) => handleChange('country', val)}
                          placeholder="e.g. GB"
                        />
                      </FieldCol>
                      <FieldCol label="Language (optional)" page={page}>
                        <ProductFormSingleTextInput
                          value={candidateForm.language}
                          handleFormValues={(val) => handleChange('language', val)}
                          placeholder="e.g. en"
                        />
                      </FieldCol>
                      <FieldCol label="Domain Name (optional)" page={page}>
                        <ProductFormSingleTextInput
                          value={candidateForm.domainName}
                          handleFormValues={(val) => handleChange('domainName', val)}
                        />
                      </FieldCol>
                    </>
                  )}
                  <Box direction="row" gap="1.5rem" wrap>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <ReadOnlyField label="Created At" value={candidate.createdAt ? buildDateTimeLabel(candidate.createdAt) : null} page={page} />
                    </Box>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <ReadOnlyField label="Updated At" value={candidate.updatedAt ? buildDateTimeLabel(candidate.updatedAt) : null} page={page} />
                    </Box>
                    <Box flex={{ grow: 1, shrink: 1 }} style={{ minWidth: 0 }}>
                      <ReadOnlyField label="Source" value={candidate.source} page={page} />
                    </Box>
                  </Box>
                  {candidate.nagraCandidateUuid && (
                    <ReadOnlyField label="NAGRA Candidate UUID" value={candidate.nagraCandidateUuid} page={page} mono />
                  )}
                </Section>
              </Box>
            </Box>
            {/* Action Buttons */}
            <Box direction="column" gap="0.5rem" pad={{ bottom: '2rem', top: '1rem' }}>
              {Object.keys(formErrors).length > 0 && (
                <Text size="0.8rem" color="var(--app-error)">
                  Please correct the highlighted fields above before saving.
                </Text>
              )}
              {candidate?.nagraResponse?.error && (
                <Box
                  background={isDarkMode ? '#7f1d1d33' : '#fef2f2'}
                  border={{ color: '#e53e3e', size: '1px' }}
                  round="6px"
                  pad={{ vertical: '0.6rem', horizontal: '0.75rem' }}
                  gap="0.25rem"
                >
                  <Text size="0.8rem" weight={600} color="#e53e3e">
                    NAGRA submission failed — please review the error and retry or reject this candidate.
                  </Text>
                  <Text size="0.75rem" color="#e53e3e" style={{ wordBreak: 'break-all' }}>
                    {typeof candidate.nagraResponse.error === 'string'
                      ? candidate.nagraResponse.error
                      : JSON.stringify(candidate.nagraResponse.error)}
                  </Text>
                </Box>
              )}
              {screenshots.length === 0 && (
                <Text size="0.8rem" color={page.hintText}>
                  A screenshot is required before this candidate can be approved and submitted.
                </Text>
              )}
              {reviewStatus === 'submitted' && (
                <Text size="0.8rem" color={page.hintText}>
                  This candidate has already been submitted and cannot be approved or rejected again.
                </Text>
              )}
              <Box direction="row" gap="0.75rem" wrap align="center">
                <AppButton
                  overrideHover
                  level="normal"
                  label={hasUnsavedChanges ? 'Save Changes *' : 'Save Changes'}
                  disabled={saving}
                  bgColor={button.primaryBg}
                  color={button.primaryText}
                  onClick={handleSave}
                />
                <AppButton
                  overrideHover
                  level="normal"
                  label={submitting ? 'Submitting...' : 'Approve & Submit'}
                  disabled={approveDisabled}
                  bgColor="#00a855"
                  color={button.primaryText}
                  onClick={() => submitCandidate(id)}
                  style={{ whiteSpace: 'nowrap', minWidth: '9rem' }}
                />
                <AppButton
                  overrideHover
                  level="normal"
                  label="Reject"
                  disabled={rejectDisabled}
                  bgColor={isDarkMode ? '#e53e3e33' : '#fef2f2'}
                  color="#e53e3e"
                  onClick={() => setIsShowingRejectModal(true)}
                />
              </Box>
            </Box>
          </>
        )}
      </Box>
      {/* Reject Modal */}
      {isShowingRejectModal && (
        <SimpleModal
          small={small}
          modalTitle="Reject Candidate"
          desktopMinWidth="26rem"
          hideLowerSection
          toggleModal={() => {
            setIsShowingRejectModal(false);
            setRejectionReason('');
            setRejectionError('');
          }}
        >
          <Box direction="column" gap="1rem" pad={{ vertical: '1.25rem' }}>
            <Text size="0.85rem" color={page.hintText}>
              Provide a reason for rejection. This will be saved on the candidate record.
            </Text>
            <Box direction="column" gap="0.4rem">
              <StyledFormTextArea
                placeholder="Describe why this candidate is being rejected..."
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  if (rejectionError) setRejectionError('');
                }}
                resize="vertical"
                style={{ minHeight: '90px', borderColor: rejectionError ? 'var(--app-error)' : undefined }}
              />
              {rejectionError && (
                <Text size="0.75rem" color="var(--app-error)">{rejectionError}</Text>
              )}
            </Box>
            <Box align="end">
              <AppButton
                overrideHover
                level="normal"
                label="Confirm Rejection"
                disabled={saving}
                bgColor="#e53e3e"
                color="#ffffff"
                onClick={handleReject}
              />
            </Box>
          </Box>
        </SimpleModal>
      )}
    </AdminNavWrapper>
  );
};

/* eslint-disable camelcase */
AdminNagraCandidateDetailPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  candidate: PropTypes.shape({
    reviewStatus: PropTypes.string,
    screenshotS3Urls: PropTypes.arrayOf(PropTypes.string),
    videoUrl: PropTypes.shape({ url: PropTypes.string }),
    referenceUrl: PropTypes.shape({ url: PropTypes.string }),
    viewingPageUrl: PropTypes.shape({ url: PropTypes.string }),
    playerPageUrl: PropTypes.shape({ url: PropTypes.string }),
    type: PropTypes.string,
    channelName: PropTypes.string,
    origin: PropTypes.string,
    playCommand: PropTypes.string,
    source: PropTypes.string,
    wasEditedByQa: PropTypes.bool,
    rejectionReason: PropTypes.string,
    nagraCandidateUuid: PropTypes.string,
    nagraResponse: PropTypes.shape({
      error: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    }),
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    metadata: PropTypes.shape({
      contentType: PropTypes.string,
      country: PropTypes.string,
      language: PropTypes.string,
      domainName: PropTypes.string,
      serverName: PropTypes.string,
      serverLocation: PropTypes.string,
      searchString: PropTypes.string,
      nexguardWatermarkId: PropTypes.string,
      identificationTimestamp: PropTypes.string,
      subscribers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      channelStatus: PropTypes.string,
      claimType: PropTypes.string,
      viewsDownloads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      videoDuration: PropTypes.string,
      infringementPublishingTimestamp: PropTypes.string,
      channelType: PropTypes.string,
    }),
  }),
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  uploadingScreenshot: PropTypes.bool.isRequired,
  fetchCandidate: PropTypes.func.isRequired,
  saveCandidate: PropTypes.func.isRequired,
  saveCandidateStatus: PropTypes.func.isRequired,
  uploadScreenshot: PropTypes.func.isRequired,
  submitCandidate: PropTypes.func.isRequired,
};
/* eslint-enable camelcase */

function mapStateToProps(state) {
  return {
    candidate: state.nagraCandidates.candidate,
    loading: state.nagraCandidates.loading,
    saving: state.nagraCandidates.saving,
    submitting: state.nagraCandidates.submitting,
    uploadingScreenshot: state.nagraCandidates.uploadingScreenshot,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchCandidate: fetchNagraCandidate,
      saveCandidate: updateNagraCandidate,
      saveCandidateStatus: updateNagraCandidateStatus,
      uploadScreenshot: uploadNagraScreenshot,
      submitCandidate: submitNagraCandidate,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminNagraCandidateDetailPage);
